import type { ReactNode } from 'react'
import { Button } from './Button'
import type { FeaturedIconColor } from './FeaturedIcon'
import { IconAlert } from './icons'
import { Modal } from './Modal'
import { ErrorState } from './States'

export interface ConfirmDialogProps {
  open: boolean
  onClose: () => void
  onConfirm: () => void
  title: ReactNode
  /** Say what will actually happen, including anything that cannot be undone. */
  description: ReactNode
  confirmLabel?: string
  cancelLabel?: string
  tone?: 'primary' | 'danger'
  loading?: boolean
  /** Rendered above the buttons when the confirm attempt itself failed. */
  error?: unknown
  children?: ReactNode
  /** The glyph in the header tile. A warning triangle when left out. */
  icon?: ReactNode
  /** The tile's colour family. Warning when left out, which is what the design draws. */
  iconTone?: FeaturedIconColor
  /**
   * What the left button does, when it is a choice in its own right rather
   * than a way back. `Discard changes` in the unsaved-changes confirm is the
   * case: Escape and the scrim still mean "stay", which is `onClose`.
   */
  onCancel?: () => void
}

/**
 * A yes or no gate in front of something consequential. It never closes itself
 * while the action is running, so a half-finished mutation cannot be orphaned.
 *
 * The stacked header with a warning tile and two equal buttons, secondary left
 * and the action right, as every confirm in the design is drawn.
 */
export function ConfirmDialog({
  open,
  onClose,
  onConfirm,
  title,
  description,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  tone = 'primary',
  loading = false,
  error,
  children,
  icon,
  iconTone = 'warning',
  onCancel,
}: ConfirmDialogProps) {
  const hasBody = (children !== undefined && children !== null && children !== false) || Boolean(error)
  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      description={description}
      header="stacked"
      icon={icon ?? <IconAlert />}
      iconTone={iconTone}
      size="sm"
      dismissible={!loading}
      footerLayout="split"
      footer={
        <>
          {/*
            Focus lands on `data-primary` when the dialog opens. On a
            destructive confirm that is the way back, not the action, so a
            reflexive Enter keeps the thing rather than deleting it.
          */}
          <Button
            variant="secondary"
            onClick={onCancel ?? onClose}
            disabled={loading}
            data-primary={tone === 'danger' ? '' : undefined}
          >
            {cancelLabel}
          </Button>
          <Button
            variant={tone === 'danger' ? 'danger' : 'primary'}
            onClick={onConfirm}
            loading={loading}
            data-primary={tone === 'danger' ? undefined : ''}
          >
            {confirmLabel}
          </Button>
        </>
      }
    >
      {hasBody ? (
        <div className="space-y-3">
          {children}
          {error ? <ErrorState error={error} /> : null}
        </div>
      ) : null}
    </Modal>
  )
}
